import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Zap, Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import thunderFoxLogo from "/fox-logo.png"; // Ensure this path is correct in your project

const customStyles = `
  .nav-glass {
    background: rgba(2, 6, 23, 0.85);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  }
  
  .text-glow-hover:hover {
    text-shadow: 0 0 10px rgba(34, 211, 238, 0.8);
  }

  .nav-item-active {
    text-shadow: 0 0 15px rgba(168, 85, 247, 0.6);
  }

  .btn-storm {
    background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
    position: relative;
    overflow: hidden;
    transition: all 0.3s ease;
  }
  
  .btn-storm::before {
    content: '';
    position: absolute;
    top: 0; left: -100%;
    width: 100%; height: 100%;
    background: linear-gradient(90deg, transparent, rgba(56, 189, 248, 0.4), transparent);
    transition: left 0.5s;
  }
  
  .btn-storm:hover::before {
    left: 100%;
  }
`;

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/about", label: "About" },
    { path: "/services", label: "Services" },
    { path: "/gameplay", label: "Arena" },
    { path: "/colorplay", label: "Spectrum" },
    { path: "/contact", label: "Contact" },
  ];

  const isActive = (path: string) => location.pathname === path;

  // Detect scroll to adjust navbar appearance slightly
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "py-0 shadow-[0_4px_30px_rgba(0,0,0,0.5)]" : "py-2"
      } nav-glass`}
    >
      <style>{customStyles}</style>
      
      {/* Top electric line decoration */}
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent opacity-50" />

      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          
          {/* Logo Section */}
          <Link 
            to="/" 
            className="flex items-center space-x-3 group relative"
          >
            <div className="relative w-12 h-12 flex items-center justify-center bg-slate-800/50 rounded-xl border border-white/10 group-hover:border-cyan-500/50 transition-colors overflow-hidden">
               {/* Animated background sparkle behind logo */}
               <div className="absolute inset-0 bg-cyan-500/20 opacity-0 group-hover:opacity-100 transition-opacity blur-md" />
               <img src={thunderFoxLogo} alt="Thunder Fox" className="h-8 w-8 relative z-10" />
            </div>
            
            <div className="hidden md:block">
              <h1 className="text-xl font-black tracking-tight text-white group-hover:text-cyan-400 transition-colors flex items-center gap-1">
                Thunder<span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-500">Fox</span>
                <Zap className="w-4 h-4 text-yellow-400 fill-yellow-400 opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all duration-300" />
              </h1>
              <p className="text-[0.7rem] text-slate-400 uppercase tracking-widest font-semibold">Web Solutions</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="relative px-4 py-2 group"
              >
                <span className={`relative z-10 text-sm font-bold transition-colors duration-300 ${
                  isActive(link.path)
                    ? "text-white nav-item-active"
                    : "text-slate-400 group-hover:text-cyan-300 text-glow-hover"
                }`}>
                  {link.label}
                </span>

                {/* Active State: Electric Beam */}
                {isActive(link.path) && (
                  <motion.div
                    layoutId="navbar-indicator"
                    className="absolute inset-0 rounded-lg bg-gradient-to-b from-white/5 to-white/10 border border-white/5"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                  >
                     <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1/2 h-[2px] bg-gradient-to-r from-transparent via-cyan-400 to-transparent shadow-[0_0_10px_#22d3ee]" />
                  </motion.div>
                )}
                
                {/* Hover State: Subtle Glow */}
                {!isActive(link.path) && (
                    <div className="absolute inset-0 bg-white/5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 scale-90 group-hover:scale-100" />
                )}
              </Link>
            ))}
          </div>

          {/* CTA Button & Mobile Toggle */}
          <div className="flex items-center gap-4">
            <Link
              to="/contact"
              className="hidden md:flex btn-storm items-center gap-2 px-6 py-2.5 rounded-lg border border-cyan-500/30 text-cyan-50 font-bold text-sm shadow-[0_0_15px_rgba(6,182,212,0.1)] hover:shadow-[0_0_20px_rgba(6,182,212,0.4)] hover:border-cyan-400/60"
            >
              <Sparkles className="w-4 h-4 text-yellow-300" />
              <span>Initialize</span>
            </Link>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="md:hidden p-2 text-slate-300 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0, borderTopWidth: 0 }}
            animate={{ opacity: 1, height: "auto", borderTopWidth: 1 }}
            exit={{ opacity: 0, height: 0, borderTopWidth: 0 }}
            className="md:hidden bg-slate-950/95 backdrop-blur-xl border-t border-white/10 overflow-hidden"
          >
            <div className="container mx-auto px-4 py-6 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`flex items-center justify-between px-4 py-3 rounded-xl transition-all ${
                    isActive(link.path)
                      ? "bg-cyan-950/30 text-cyan-400 border border-cyan-500/30"
                      : "text-slate-400 hover:bg-white/5 hover:text-white"
                  }`}
                >
                  <span className="font-bold">{link.label}</span>
                  {isActive(link.path) && <Zap className="w-4 h-4 animate-pulse" />}
                </Link>
              ))}
              
              <div className="pt-4 mt-4 border-t border-white/10">
                <Link
                  to="/contact"
                  onClick={() => setIsOpen(false)}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 rounded-xl text-white font-bold shadow-lg"
                >
                    <Sparkles className="w-4 h-4" />
                    Initialize System
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;